/**
 * Word Timestamp Synchronization Utilities
 * 
 * Handles preserving and interpolating word timestamps during text editing
 */

/**
 * Normalize text for comparison (remove extra whitespace, lowercase)
 */
const normalizeText = (text) => {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
};

/**
 * Calculate similarity between two strings (simple Levenshtein-based approach)
 */
const calculateSimilarity = (str1, str2) => {
  const s1 = normalizeText(str1);
  const s2 = normalizeText(str2);
  
  if (s1 === s2) return 1.0;
  
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  
  if (longer.length === 0) return 1.0;
  
  // Simple substring check
  if (longer.includes(shorter)) {
    return shorter.length / longer.length;
  }
  
  return 0.5; // Default moderate similarity
};

const wordTextOf = (word) => (word?.word || word?.raw || '');

/**
 * Longest-common-subsequence alignment between the edited tokens and the
 * original words, comparing normalized word text.
 *
 * A greedy look-ahead window (the previous implementation) loses every
 * timestamp as soon as the user deletes more words than the window size:
 * the matcher never reaches the surviving originals, so all timestamps get
 * interpolated from the stale block boundaries. LCS is order-preserving and
 * indifferent to how much text was removed or inserted, so surviving words
 * keep their real Whisper timings.
 *
 * @returns {Array<number>} matchedOriginalIdx[i] = original index for edited
 *   token i, or -1 when the token has no counterpart.
 */
const alignByLcs = (editedTokens, originalWords) => {
  const n = editedTokens.length;
  const m = originalWords.length;
  const matched = new Array(n).fill(-1);

  const a = editedTokens.map(normalizeText);
  const b = originalWords.map((w) => normalizeText(wordTextOf(w)));

  // Trim the identical head and tail first. A real edit touches one spot, so
  // this usually leaves a handful of tokens for the quadratic part — and a
  // pure deletion (the case this whole path exists for) leaves nothing at all.
  let head = 0;
  while (head < n && head < m && a[head] === b[head]) {
    matched[head] = head;
    head++;
  }

  let tail = 0;
  while (tail < n - head && tail < m - head && a[n - 1 - tail] === b[m - 1 - tail]) {
    matched[n - 1 - tail] = m - 1 - tail;
    tail++;
  }

  const an = n - tail - head;
  const bn = m - tail - head;
  if (an <= 0 || bn <= 0) return matched;

  // (an+1) * (bn+1) table. Give up on the middle region if it is pathological
  // (whole block rewritten): the head/tail anchors still hold, and the rest
  // falls back to interpolation.
  if ((an + 1) * (bn + 1) > 4_000_000) return matched;

  const width = bn + 1;
  const dp = new Int32Array((an + 1) * width);

  for (let i = an - 1; i >= 0; i--) {
    for (let j = bn - 1; j >= 0; j--) {
      dp[i * width + j] = a[head + i] === b[head + j]
        ? dp[(i + 1) * width + (j + 1)] + 1
        : Math.max(dp[(i + 1) * width + j], dp[i * width + (j + 1)]);
    }
  }

  let i = 0;
  let j = 0;
  while (i < an && j < bn) {
    if (a[head + i] === b[head + j]) {
      matched[head + i] = head + j;
      i++;
      j++;
    } else if (dp[(i + 1) * width + j] >= dp[i * width + (j + 1)]) {
      i++;
    } else {
      j++;
    }
  }

  return matched;
};

/**
 * Second pass over the gaps left by LCS: a lightly edited word (typo fix,
 * casing, punctuation) has no exact match but should still keep its timing.
 * Only originals inside the same gap are candidates, so ordering is kept.
 */
const fuzzyFillGaps = (editedTokens, originalWords, matched) => {
  const n = editedTokens.length;
  const m = originalWords.length;

  let i = 0;
  while (i < n) {
    if (matched[i] !== -1) {
      i++;
      continue;
    }

    // Gap = [i, gapEnd) on the edited side, bounded by the surrounding anchors.
    let gapEnd = i;
    while (gapEnd < n && matched[gapEnd] === -1) gapEnd++;

    let prevAnchor = -1;
    for (let k = i - 1; k >= 0; k--) {
      if (matched[k] !== -1) { prevAnchor = matched[k]; break; }
    }
    const nextAnchor = gapEnd < n ? matched[gapEnd] : m;

    let originalCursor = prevAnchor + 1;
    for (let k = i; k < gapEnd; k++) {
      let bestScore = 0;
      let bestIdx = -1;
      for (let j = originalCursor; j < nextAnchor; j++) {
        const score = calculateSimilarity(editedTokens[k], wordTextOf(originalWords[j]));
        if (score > bestScore) {
          bestScore = score;
          bestIdx = j;
        }
      }
      if (bestScore > 0.7 && bestIdx >= 0) {
        matched[k] = bestIdx;
        originalCursor = bestIdx + 1;
      }
    }

    i = gapEnd;
  }

  return matched;
};

/**
 * Try to match edited words with original words to preserve timestamps
 */
export const preserveWordTimestamps = (editedText, originalWords) => {
  if (!originalWords || originalWords.length === 0) {
    return { words: [], needsInterpolation: true };
  }

  if (!editedText || !editedText.trim()) {
    return { words: [], needsInterpolation: false };
  }

  // Split edited text into words
  const editedWords = editedText.trim().split(/\s+/);

  const matched = fuzzyFillGaps(
    editedWords,
    originalWords,
    alignByLcs(editedWords, originalWords),
  );

  const result = editedWords.map((editedWord, i) => {
    const original = matched[i] >= 0 ? originalWords[matched[i]] : null;
    if (!original) {
      return {
        word: editedWord,
        raw: editedWord,
        start: null,
        end: null,
        speaker: null,
        probability: null,
        index: i,
        preserved: false,
      };
    }
    return {
      word: editedWord,
      raw: editedWord,
      start: original.start ?? null,
      end: original.end ?? null,
      speaker: original.speaker ?? null,
      probability: original.probability ?? null,
      index: i,
      preserved: true,
    };
  });

  return { words: result, needsInterpolation: result.some(w => w.start === null) };
};

/**
 * Interpolate timestamps for words that don't have them
 */
const interpolateTimestamps = (words, blockStart, blockEnd) => {
  if (!words || words.length === 0) return words;

  // Find words with timestamps
  const wordsWithTimestamps = words.filter(w => w.start !== null && w.end !== null);
  
  if (wordsWithTimestamps.length === 0) {
    // No timestamps at all - distribute evenly across block
    const totalDuration = blockEnd - blockStart;
    const wordDuration = totalDuration / words.length;
    
    return words.map((word, idx) => ({
      ...word,
      start: blockStart + (idx * wordDuration),
      end: blockStart + ((idx + 1) * wordDuration),
    }));
  }

  // Interpolate missing timestamps
  const result = [...words];
  
  for (let i = 0; i < result.length; i++) {
    if (result[i].start === null || result[i].end === null) {
      // Find nearest words with timestamps (before and after)
      let beforeIdx = -1;
      let afterIdx = -1;
      
      for (let j = i - 1; j >= 0; j--) {
        if (result[j].start !== null) {
          beforeIdx = j;
          break;
        }
      }
      
      for (let j = i + 1; j < result.length; j++) {
        if (result[j].start !== null) {
          afterIdx = j;
          break;
        }
      }

      // Interpolate based on surrounding words
      if (beforeIdx >= 0 && afterIdx >= 0) {
        // Between two known timestamps
        const beforeEnd = result[beforeIdx].end;
        const afterStart = result[afterIdx].start;
        const gap = afterStart - beforeEnd;
        const wordsInGap = afterIdx - beforeIdx;
        const wordDuration = gap / wordsInGap;
        const position = i - beforeIdx;
        
        result[i].start = beforeEnd + (position * wordDuration);
        result[i].end = beforeEnd + ((position + 1) * wordDuration);
      } else if (beforeIdx >= 0) {
        // After last known timestamp
        const lastEnd = result[beforeIdx].end;
        const remainingDuration = blockEnd - lastEnd;
        const wordsRemaining = result.length - beforeIdx - 1;
        const wordDuration = remainingDuration / wordsRemaining;
        const position = i - beforeIdx;
        
        result[i].start = lastEnd + (position * wordDuration);
        result[i].end = lastEnd + ((position + 1) * wordDuration);
      } else if (afterIdx >= 0) {
        // Before first known timestamp
        const firstStart = result[afterIdx].start;
        const durationBefore = firstStart - blockStart;
        const wordDuration = durationBefore / afterIdx;
        
        result[i].start = blockStart + (i * wordDuration);
        result[i].end = blockStart + ((i + 1) * wordDuration);
      }
    }
  }

  return result;
};

/**
 * Main function: synchronize words array with edited text
 * Preserves timestamps where possible, interpolates where necessary
 */
export const syncWordsWithText = (blockText, blockWords, blockStart, blockEnd) => {
  // First, try to preserve timestamps
  const { words, needsInterpolation } = preserveWordTimestamps(blockText, blockWords);
  
  // If interpolation is needed, do it
  if (needsInterpolation && blockStart !== undefined && blockEnd !== undefined) {
    return interpolateTimestamps(words, blockStart, blockEnd);
  }
  
  return words;
};

const toTime = (value) => {
  if (value == null || value === '') return null;
  const t = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(t) ? t : null;
};

/**
 * Derive a block's start/end from its (possibly edited) words array.
 *
 * Editing text does not change the audio, but it does change *which part* of
 * the audio the block still covers. Deleting the opening words of a block
 * means playback must start later; deleting the tail means it must stop
 * earlier. Without this, the header timestamp and every seek (`seekToBlock`,
 * play button, active-block highlight) keep pointing at audio that no longer
 * belongs to the block.
 *
 * The result is clamped to the original window: the remaining words are
 * always a subset of the block's original speech, and interpolated words are
 * derived from that same window, so a boundary can only move inward.
 *
 * @returns {{start: number|undefined, end: number|undefined}}
 */
export const deriveBlockBoundsFromWords = (words, fallbackStart, fallbackEnd) => {
  const unchanged = { start: fallbackStart, end: fallbackEnd };
  if (!Array.isArray(words) || words.length === 0) return unchanged;

  let minStart = null;
  let maxEnd = null;
  for (const word of words) {
    const s = toTime(word?.start);
    const e = toTime(word?.end);
    if (s != null && (minStart == null || s < minStart)) minStart = s;
    if (e != null && (maxEnd == null || e > maxEnd)) maxEnd = e;
  }
  if (minStart == null && maxEnd == null) return unchanged;

  const originalStart = toTime(fallbackStart);
  const originalEnd = toTime(fallbackEnd);

  let start = minStart != null ? minStart : originalStart;
  let end = maxEnd != null ? maxEnd : originalEnd;
  if (originalStart != null && start != null) start = Math.max(start, originalStart);
  if (originalEnd != null && end != null) end = Math.min(end, originalEnd);

  // Degenerate result (e.g. corrupt word timings) — keep what we had.
  if (start == null || end == null || end <= start) return unchanged;

  return { start, end };
};

/**
 * Check if text has changed significantly from words array
 */
export const hasTextChanged = (blockText, blockWords) => {
  if (!blockWords || blockWords.length === 0) return true;
  
  const reconstructedText = blockWords
    .map(w => w.word || w.raw)
    .join(' ')
    .trim();
  
  const normalizedBlock = normalizeText(blockText);
  const normalizedReconstructed = normalizeText(reconstructedText);
  
  return normalizedBlock !== normalizedReconstructed;
};

